import { type Tool, ToolType } from "@repo/app-types/entities/tool";
import {
  IconPhone,
  IconPhoneEnd,
  IconSearch,
  IconTools,
} from "@tabler/icons-react";
import type { FC } from "react";
import { Button } from "react-aria-components";
import { twMerge } from "tailwind-merge";

export interface ToolsTableProps {
  tools: readonly Tool[];
  isLoading?: boolean;
  onToolClick: (tool: Tool) => void;
}

interface ToolTypeConfig {
  icon: React.ComponentType<{ className?: string }>;
  color: string;
}

const toolTypeConfigMap: Record<ToolType, ToolTypeConfig> = {
  [ToolType.CALL_TRANSFER]: {
    icon: IconPhone,
    color: "bg-blue-500",
  },
  [ToolType.CALL_END]: {
    icon: IconPhoneEnd,
    color: "bg-red-500",
  },
  [ToolType.CUSTOM]: {
    icon: IconTools,
    color: "bg-purple-500",
  },
  [ToolType.WEB_DOCUMENT_RESEARCH]: {
    icon: IconSearch,
    color: "bg-emerald-500",
  },
};

const ToolRow: FC<{ tool: Tool; onClick: () => void }> = ({
  tool,
  onClick,
}) => {
  const config = toolTypeConfigMap[tool.type];
  const Icon = config.icon;

  return (
    <Button
      className="flex w-full items-center gap-4 bg-white px-4 py-2 text-left first:rounded-t-lg last:rounded-b-lg hover:bg-blue-50"
      onClick={onClick}
    >
      <div
        className={twMerge(
          "flex h-6 w-6 shrink-0 items-center justify-center rounded-lg",
          config.color,
        )}
      >
        <Icon className="size-4 text-white" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-foreground">{tool.name}</div>
        <div className="truncate text-xs text-muted-foreground">
          {tool.description}
        </div>
      </div>
    </Button>
  );
};

export const ToolsTable: FC<ToolsTableProps> = ({
  tools,
  isLoading,
  onToolClick,
}) => {
  if (isLoading) {
    return (
      <div className="divide-y divide-border-light rounded-lg border border-border-light">
        {[1, 2].map((i) => (
          <div
            key={i}
            className="flex w-full items-center gap-4 bg-white px-4 py-2 text-left first:rounded-t-lg last:rounded-b-lg"
          >
            <div className="h-6 w-6 animate-pulse rounded-sm bg-gray-200" />
            <div className="min-w-0 flex-1">
              <div className="mb-1 h-4 w-36 animate-pulse rounded-sm bg-gray-200" />
              <div className="h-4 w-48 animate-pulse rounded-sm bg-gray-200" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (tools.length === 0) {
    return null;
  }

  return (
    <div className="divide-y divide-border-light rounded-lg border border-border-light">
      {tools.map((tool) => (
        <ToolRow key={tool.id} tool={tool} onClick={() => onToolClick(tool)} />
      ))}
    </div>
  );
};
